import {
  ActivityType,
  BolusState,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { FavoriteFillIcon } from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useGraphContext } from "../../GraphContext"
import type { GraphActivity } from "./GraphActivity"
import styles from "./GraphActivityComponent.module.scss"
import { GraphActivityTitle } from "./GraphActivityTitle"
import { ActivityIcon } from "src/components"
import { useThisMinute } from "src/hooks"

interface Props {
  containerStartTime: string;
  activity: GraphActivity;
}

export const extraMarkerHeight = 6

/*
 * this renders the icon and text for an activity on the graph
 */
export function GraphActivityComponent(props: Props) {
  const {
    activity, containerStartTime,
  } = props

  const {
    graphActivityHeight,
    graphIconSize,
    pointsPerHour,
    zoom,
  } = useGraphContext()

  const left = useMemo(
    () => dayjs(activity.startedAt).diff(
      containerStartTime,
      "hours",
      true,
    ) *
      pointsPerHour,
    [
      activity.startedAt,
      containerStartTime,
      pointsPerHour,
    ],
  )

  const isOngoingEvent = useMemo(
    () => {
      switch (activity.activityType) {
        case ActivityType.PumpSuspension:
          return activity.pump_suspension.state === PumpSuspendState.InProgress
        case ActivityType.Bolus:
          return activity.bolus.state === BolusState.InProgress
        default:
          return false
      }
    },
    [activity],
  )

  const thisMinute = useThisMinute()

  const duration = useMemo(
    () => {
      let durationInSeconds = activity.durationInSeconds

      if (isOngoingEvent) {
      // for an ongoing suspension, the duration is from the start time to now
      // (plus the graph zoom padding so the line reaches the right edge)
        durationInSeconds = dayjs(thisMinute)
          .add(
            zoom,
            "minutes",
          )
          .diff(
            activity.startedAt,
            "seconds",
            true,
          )
      }

      const rawDuration = (durationInSeconds * pointsPerHour) / 3600
      return isNaN(rawDuration) || rawDuration < 0 ? 0 : rawDuration
    },
    [
      activity.durationInSeconds,
      activity.startedAt,
      isOngoingEvent,
      pointsPerHour,
      zoom,
      thisMinute,
    ],
  )

  const iconProps = useMemo(
    () => ({
      className: styles.activityIcon,
      height: graphIconSize,
      width: graphIconSize,
    }),
    [graphIconSize],
  )

  const markerHeight = graphActivityHeight + extraMarkerHeight
  const arrowWingLength = (graphActivityHeight - 4) / 2
  const verticalCenter = graphActivityHeight / 2

  return (
    <div
      className={styles.container}
      style={{
        left,
        height: graphActivityHeight,
      }}
    >
      <div
        className={styles.timeMarker}
        style={{
          height: markerHeight,
        }}
      />
      {duration > 0 && (
        <>
          <div
            className={styles.durationLine}
            style={{
              top: verticalCenter,
              width: duration,
            }}
          />
          {isOngoingEvent && (
            <>
              <div
                className={styles.durationArrowTop}
                style={{
                  left: duration,
                  top: verticalCenter,
                  width: arrowWingLength,
                }}
              />
              <div
                className={styles.durationArrowBottom}
                style={{
                  left: duration,
                  top: verticalCenter,
                  width: arrowWingLength,
                }}
              />
            </>
          )}
          {!isOngoingEvent && (
            <div
              className={styles.durationMarker}
              style={{
                left: duration,
                height: markerHeight,
              }}
            />
          )}
        </>
      )}
      <button
        className={styles.activity}
        style={{ borderRadius: graphActivityHeight / 2 }}
        onClick={activity.onClick}
        id={activity.entryTemplateId ? "favorite-graph-icon" : `${activity.activityType}-graph-icon`}
      >
        {activity.entryTemplateId ? (
          <FavoriteFillIcon {...iconProps} />
        ) : (
          activity.activityType && (
            <ActivityIcon
              id="graph-activity-icon"
              fillIcon
              activityType={activity.activityType}
              suspendReason={
                activity.activityType === ActivityType.PumpSuspension
                  ? activity.pump_suspension.suspend_reason
                  : undefined
              }
              {...iconProps}
            />
          )
        )}
        <GraphActivityTitle activity={activity} />
      </button>
    </div>
  )
}
